/* ==================================================================
   Tipos do motor — Reflect On Engenharia
   ------------------------------------------------------------------
   O motor tem DOIS consumidores com autoridades diferentes:

     · o navegador, que mostra a conta abrindo ao vivo — autoridade NENHUMA
     · o servidor, que grava o pedido e monta a sessão do Stripe — autoridade TOTAL

   Estes tipos existem pra que o limite entre os dois seja explícito no
   código, e não uma combinação verbal. Repare no que `Respostas` NÃO
   tem: nenhum campo de preço. O cliente responde perguntas; quem faz
   conta é o motor. Preço que chega do navegador não existe como tipo,
   então não existe como possibilidade.
   ================================================================== */

/** O que a reforma vai mexer. `nenhum` é a saída de quem não precisa de ART. */
export type Escopo =
  | "piso"
  | "pintura"
  | "ar"
  | "vidro"
  | "parede"
  | "hidraulica"
  | "eletrica"
  | "gas"
  | "nenhum";

/** Lista fechada, usada pra validar o que chega de fora. Manter em sincronia
 *  com as opções do passo `escopo` em `passos.ts` — `npm run conferir` falha
 *  se as duas divergirem, que é o jeito de descobrir isso agora e não quando
 *  um escopo novo passar batido pela validação da rota. */
export const ESCOPOS: readonly Escopo[] = [
  "piso",
  "pintura",
  "ar",
  "vidro",
  "parede",
  "hidraulica",
  "eletrica",
  "gas",
  "nenhum",
] as const;

export type Estrutural = "sim" | "nao" | "naosei";
export type Faixa = "ate15" | "acima15";
export type Acompanha = "sim" | "nao";

/** O que o cliente respondeu. É a ÚNICA coisa que o servidor aceita do
 *  navegador — tudo o mais ele calcula. */
export type Respostas = {
  escopo: Escopo[];
  /** só perguntado quando `parede` está no escopo */
  estrutural?: Estrutural;
  /** faixa declarada na triagem; sobrescrita por `valorNum` quando ele existe */
  valor?: Faixa;
  /** valor da obra declarado no cadastro, em reais */
  valorNum?: number;
  acompanha?: Acompanha;
  /** `undefined` é quem ainda não opinou, e esse caso MANTÉM o memorial.
   *  Só `false` conta como "o cliente tirou". */
  memorial?: boolean;
};

export type Via = "LIMPO" | "ANALISE" | "RECUSA";

/** As três portas de saída. Recusa é dado valioso, não desperdício: mede
 *  quanta gente chega pedindo o que o Alan não vende, e isso é pauta de
 *  conteúdo e de SEO. Por isso o motivo é tipado e vai gravado. */
export type MotivoRecusa = "sem_art" | "estrutural" | "acompanhamento";

/** Por que o engenheiro olha antes de cobrar. */
export type MotivoAnalise = "escopo" | "estrutural_incerto" | "porte";

export type ResultadoRecusa = {
  via: "RECUSA";
  motivo: MotivoRecusa;
  titulo: string;
  texto: string;
  cobra: false;
};

export type ResultadoOrcamento = {
  via: "LIMPO" | "ANALISE";
  /** `null` na via LIMPO, que não tem motivo pra existir */
  motivo: MotivoAnalise | null;
  servico: number;
  taxa: number;
  memorial: boolean;
  /** o caso pede o aviso de "o síndico costuma pedir" antes de deixar tirar */
  alertaMemorial: boolean;
  total: number;
  titulo: string;
  texto: string;
  cobra: boolean;
  /** qual linha da tabela do CREA gerou esta taxa. `null` enquanto a tabela
   *  for constante de código; vira id do banco no bloco 2. É o que torna
   *  possível explicar, seis meses depois, por que o pedido custou aquilo. */
  tabelaCreaId: string | null;
};

export type Resultado = ResultadoRecusa | ResultadoOrcamento;

/** Estreitamento de tipo pra quem consome o resultado sem saber a via. */
export function temOrcamento(r: Resultado): r is ResultadoOrcamento {
  return r.via !== "RECUSA";
}
